import BaseButton from '@/components/ui/BaseButton';
import BaseDialog from '@/components/ui/BaseDialog';
import { ChangeEvent, useCallback, useState } from 'react';
import BaseSelect from '@/components/form/BaseSelect';
import { Upload } from '@mui/icons-material';
import { FormControl, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StoreSettings } from '@/types/pages/stores';

interface ImportDialogProps {
    isOpen: boolean;
    importLoading: boolean;
    storeSettings: StoreSettings | null;
    onImport: (e: ChangeEvent<HTMLInputElement>, fileType: string) => void;
    onClose: () => void;
}

const defaultOptions = [{ value: 'aura-json', label: 'AURA JSON' }];
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const ImportDialog = ({ isOpen, importLoading, storeSettings, onImport, onClose }: ImportDialogProps) => {
    const { t } = useTranslation('common');
    const [fileType, setFileType] = useState('aura-json');
    const [options, setOptions] = useState<{ label: string; value: string }[]>(defaultOptions);

    const handleOnEnter = useCallback(() => {
        const customerName = storeSettings?.customer_name || '';
        if (customerName === 'family-mart') {
            setOptions([...defaultOptions, { value: 'familyMart-tfm-csv', label: 'Family Mart TFM CSV' }]);
        } else if (customerName === 'lotuss-thailand') {
            setOptions([...defaultOptions, { value: 'lotuss-thailand', label: 'Lotuss Thailand CSV' }]);
        } else if (customerName === 'taiwan-mobile') {
            setOptions([...defaultOptions, { value: 'taiwan-mobile', label: 'Taiwan Mobile XLSX' }]);
        }
    }, [storeSettings]);

    const handleOnExited = useCallback(() => {
        setOptions(defaultOptions);
        setFileType('aura-json');
    }, []);

    const getFileType = () => {
        if (fileType === 'familyMart-tfm-csv' || fileType === 'lotuss-thailand') {
            return '.csv';
        }
        if (fileType === 'taiwan-mobile') {
            return '.xlsx';
        }
        return '.json';
    };

    return (
        <BaseDialog
            open={isOpen}
            dialogTitle={t('Import Products')}
            onClose={onClose}
            TransitionProps={{
                onEnter: handleOnEnter,
                onExited: handleOnExited,
            }}
        >
            <FormControl fullWidth sx={{ mt: 1 }}>
                <BaseSelect
                    label={t('Select File Type')}
                    value={fileType}
                    onChange={(e) => {
                        setFileType(e.target.value as string);
                    }}
                    options={options}
                />
                <BaseButton
                    component="label"
                    variant="contained"
                    color="primary"
                    startIcon={<Upload />}
                    loading={importLoading}
                    sx={{ mt: 2 }}
                >
                    {t('Import')}
                    <VisuallyHiddenInput
                        type="file"
                        accept={getFileType()}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onImport(e, fileType)}
                    />
                </BaseButton>
            </FormControl>
        </BaseDialog>
    );
};

export default ImportDialog;
